import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { leadsAPI, businessTypePublicAPI } from '../../api/apiService';

import { toast } from 'react-hot-toast';
import { Send, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DemoRequestModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        business_name: '',
        contact_person: '',
        phone: '',
        email: '',
        business_type: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [showOtherInput, setShowOtherInput] = useState(false);

    React.useEffect(() => {
        fetchBusinessTypes();
    }, []);

    const fetchBusinessTypes = async () => {
        try {
            const res = await businessTypePublicAPI.getAll();
            // Sort: Alpha but "Other" always last
            const sorted = res.data.sort((a, b) => {
                if (a.name === "Other") return 1;
                if (b.name === "Other") return -1;
                return a.name.localeCompare(b.name);
            });
            setBusinessTypes(sorted);
        } catch (error) {
            console.error("Failed to fetch business types:", error);
        }
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await leadsAPI.submitRequest(formData);
            setSubmitted(true);
            toast.success("Demo request submitted successfully!");
            setTimeout(() => {
                onClose();
                setSubmitted(false);
                setFormData({
                    business_name: '',
                    contact_person: '',
                    phone: '',
                    email: '',
                    business_type: '',
                    message: ''
                });
            }, 3000);
        } catch (error) {
            console.error("Error submitting demo request:", error);
            toast.error("Failed to submit request. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] p-0 border-none bg-transparent shadow-none overflow-visible">
                <AnimatePresence mode="wait">
                    {!submitted ? (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-emerald-100 dark:border-slate-800 flex flex-col max-h-[85vh] my-2"
                        >
                            <DialogHeader className="p-4 pb-2 bg-emerald-50 dark:bg-emerald-900/20 text-left rounded-t-2xl sticky top-0 z-10 border-b border-emerald-100 dark:border-slate-800">
                                <div className="pr-6">
                                    <DialogTitle className="text-xl font-black text-slate-900 dark:text-white mb-0.5">
                                        Get a Free <span className="text-emerald-600">Demo</span>
                                    </DialogTitle>
                                    <DialogDescription className="text-slate-600 dark:text-slate-400 text-[11px] font-medium leading-tight">
                                        Fill out the form below and we'll get back to you within 24h.
                                    </DialogDescription>
                                </div>
                            </DialogHeader>

                            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 pt-4">
                                <form id="demo-request-form" onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <Label htmlFor="contact_person" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Your Name *</Label>
                                            <Input
                                                id="contact_person"
                                                name="contact_person"
                                                value={formData.contact_person}
                                                onChange={handleChange}
                                                required
                                                placeholder="John Doe"
                                                className="h-10 text-sm rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="phone" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Phone Number *</Label>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                                placeholder="+91 9876543210"
                                                className="h-10 text-sm rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <Label htmlFor="business_name" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Business Name *</Label>
                                            <Input
                                                id="business_name"
                                                name="business_name"
                                                value={formData.business_name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Retail Solutions"
                                                className="h-10 text-sm rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="business_type" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Business Type</Label>
                                            <select
                                                id="business_type"
                                                name="business_type"
                                                value={formData.business_type}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setFormData(prev => ({ ...prev, business_type: val }));
                                                    setShowOtherInput(val === "Other");
                                                }}
                                                className="w-full h-10 px-3 text-sm rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-slate-900 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                                            >
                                                <option value="">Select Business Type</option>
                                                {businessTypes.map(type => (
                                                    <option key={type.id} value={type.name}>{type.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {showOtherInput && (
                                        <motion.div 
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="space-y-1.5"
                                        >
                                            <Label 
                                                htmlFor="other_business_type" 
                                                className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                                                style={{ fontFamily: "'Lexend', sans-serif" }}
                                            >
                                                Please specify business type
                                            </Label>
                                            <Input
                                                id="other_business_type"
                                                name="other_business_type"
                                                onChange={(e) => setFormData(prev => ({ ...prev, business_type: e.target.value }))}
                                                placeholder="Enter your business type"
                                                className="h-10 text-sm rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 italic"
                                                style={{ fontFamily: "'Lexend', sans-serif" }}
                                            />
                                        </motion.div>
                                    )}


                                    <div className="space-y-1.5">
                                        <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Email Address</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="john@example.com"
                                            className="h-10 text-sm rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="message" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-0.5">Additional Requirements</Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows={2}
                                            placeholder="What are you looking for?"
                                            className="rounded-lg bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 min-h-[70px] text-sm py-2"
                                        />
                                    </div>
                                </form>
                            </div>

                            <div className="p-5 border-t border-emerald-50 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-2xl">
                                <Button
                                    form="demo-request-form"
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-base font-bold shadow-lg shadow-emerald-500/10 transition-all flex items-center justify-center gap-2"
                                >
                                    {loading ? "Sending..." : "Submit Request"}
                                    {!loading && <Send size={18} />}
                                </Button>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white dark:bg-slate-900 rounded-3xl p-12 text-center shadow-2xl border border-emerald-100 dark:border-slate-800"
                        >
                            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center text-emerald-600 mx-auto mb-6">
                                <CheckCircle2 size={48} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Request Received!</h3>
                            <p className="text-slate-600 dark:text-slate-400 font-medium">
                                Thank you for your interest. Our team will contact you shortly to schedule your personalized demo.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>
        </Dialog>
    );
};

export default DemoRequestModal;
